import { useEffect, useMemo, useState } from 'react';
import { EntityUIState } from '../../../core/interfaces/entitiy-ui-state';
import { getBackendErrorCodeFromObj } from '../../../core/utils/request';
import { SchemaFieldsFactory } from '../../destination/factories/destinationFactory';
import { TARGET_AUTHORISATION_FAILED_ERROR_CODE } from '../constants';
import { Target } from '../models/target';
import { TargetObjectMeta } from '../models/target-object-meta';
import { TargetTypesEnum } from '../models/target-type';
import { findTargetObjectInGroupedList } from '../utils';
import { useQuery } from '../../../core/hooks/useQuery';
import ActivateTargetAPI from '../ActivateTargetAPI';

export function useObjectExplorer({
  target,
  currentTargetObject,
  targetObjectChanged
}: {
  target: Target;
  currentTargetObject: TargetObjectMeta | string;
  targetObjectChanged: Function;
}) {
  const [selectedTargetObject, setSelectedTargetObject] = useState<TargetObjectMeta>(null);

  const {
    fetch: fetchObjects,
    data: objects,
    uiState: fetchObjectsUIState,
    error: fetchObjectsError
  } = useQuery({
    dataSource$: () => {
      if (target.targetType === TargetTypesEnum.MAILCHIMP) {
        return ActivateTargetAPI.getMailchimpTargetObjects(target.id);
      }

      return ActivateTargetAPI.getTargetObjects(target.id);
    },
    emptyAssertion: (d) => !d?.length,
    pollInterval: 60000,
    fetchSuccess: (data) => {
      if (currentTargetObject) {
        const targetObjectToUpdate = data.find((_object) => {
          if (typeof currentTargetObject === 'string') {
            return _object.apiName === selectedTargetObject;
          }

          return _object.apiName === currentTargetObject.apiName;
        });

        if (targetObjectToUpdate) {
          selectTargetObject(targetObjectToUpdate);
        }
      }
    }
  });

  useEffect(() => {
    fetchObjects();
  }, []);

  const {
    fetch: fetchFields,
    data: rawFieldsData,
    uiState: fetchFieldsUIState,
    error: fetchingFieldsError
  } = useQuery({
    dataSource$: (targetObject: TargetObjectMeta) => ActivateTargetAPI.getTargetObjectFields(
      target.id,
      targetObject.apiName
    ).then((res) => res.data),
    emptyAssertion: (d) => !d.target_object_fields?.length,
    pollInterval: 60000
  });

  const selectTargetObject = (targetObject: TargetObjectMeta) => {
    setSelectedTargetObject(targetObject);
    targetObjectChanged(targetObject);
    fetchFields(targetObject);
  };

  const fields = useMemo(() => {
    return SchemaFieldsFactory(rawFieldsData?.target_object_fields) || [];
  }, [ rawFieldsData ]);

  const needTargetReauth = useMemo(() => {
    if (!fetchingFieldsError && !fetchObjectsError) {
      return false;
    }

    const fetchFieldsErrorCode = getBackendErrorCodeFromObj(fetchingFieldsError);
    const fetchObjectsErrorCode = getBackendErrorCodeFromObj(fetchObjectsError);

    return TARGET_AUTHORISATION_FAILED_ERROR_CODE.includes(fetchFieldsErrorCode)
      || TARGET_AUTHORISATION_FAILED_ERROR_CODE.includes(fetchObjectsErrorCode);
  }, [ fetchingFieldsError, fetchObjectsError ]);

  const lastRefreshedTs = rawFieldsData?.last_refreshed_ts || null;
  const loadingFields = fetchFieldsUIState === EntityUIState.LOADING;
  const loadingObjects = fetchObjectsUIState === EntityUIState.LOADING;

  return {
    objects,
    loadingObjects,
    loadingFields,
    needTargetReauth,
    fetchingFieldsError,
    fields,
    lastRefreshedTs,
    selectedTargetObject,
    selectTargetObject
  };
}
